import { useEffect, useState } from "react";
import client from "../api/client";
import { Shield, ChevronDown, ChevronUp, Save } from "lucide-react";
import { logEmployeeActivity } from "../utils/activityLogger";
import "./RoleManagement.css";
import { APP_PERMISSION_PAGES, PERMISSION_PAGES, getPermissionLabel } from "../config/permissionPages";

export default function RoleManagement() {
  const [employees, setEmployees] = useState([]);
  const [openPanel, setOpenPanel] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    client
      .get("/users")
      .then((res) => setEmployees(res.data || []))
      .catch(() => setEmployees([]));
  }, []);

  function parsePermissionList(value) {
    if (Array.isArray(value)) {
      return value;
    }

    try {
      const parsed = JSON.parse(value || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function openPermission(user, type) {
    if (openPanel?.userId === user.id && openPanel?.type === type) {
      setOpenPanel(null);
      setSelectedUser(null);
      return;
    }

    setSelectedUser(user);
    setOpenPanel({ userId: user.id, type });
    setPermissions(parsePermissionList(user[type]));
  }

  function togglePermission(p) {
    setPermissions(prev =>
      prev.includes(p)
        ? prev.filter(x => x !== p)
        : [...prev, p]
    );
  }

  async function savePermissions() {
    if (!selectedUser || !openPanel?.type) {
      return;
    }

    const isAppPermissions = openPanel.type === "app_permissions";
    const endpoint = isAppPermissions ? "/users/app-permissions" : "/users/permissions";

    await client.post(endpoint, {
      userId: selectedUser.id,
      permissions,
    });

    void logEmployeeActivity({
      targetEmployeeId: selectedUser.id,
      targetEmployeeName: selectedUser.name || `Employee #${selectedUser.id}`,
      action: isAppPermissions ? "App Permissions Updated" : "Permissions Updated",
      module: "Role Management",
      description: `Updated ${isAppPermissions ? "app permissions" : "permissions"} for ${selectedUser.name || `Employee #${selectedUser.id}`}.`,
    });

    alert(isAppPermissions ? "App permissions saved!" : "Permissions saved!");
    setOpenPanel(null);
    setSelectedUser(null);
    // Refresh employees list
    client
      .get("/users")
      .then((res) => setEmployees(res.data || []))
      .catch(() => {});
  }

  const permissionPagesToRender =
    openPanel?.type === "app_permissions" ? APP_PERMISSION_PAGES : PERMISSION_PAGES;

  return (
    <div className="role-page page-container">
      <header className="role-header">
        <div className="header-left">
          <h1 className="header-title">
            <Shield size={24} strokeWidth={2} />
            Role Management
          </h1>
          <p className="header-subtitle">Manage employee permissions and access roles for different pages.</p>
        </div>
      </header>

      <div className="role-main-card">
        <div className="role-section-head">
          <h2 className="section-title">Employee Permissions</h2>
          <span className="role-count-badge">{employees.length} Team Members</span>
        </div>
        <div className="employee-list">
          {employees.length > 0 ? (
            employees.map(u => (
              <div key={u.id} className="employee-card">
                <div className="employee-topbar">
                  <div className="employee-info">
                    <div className="employee-avatar">
                      {(u.name || `U${u.id}`).trim().charAt(0).toUpperCase()}
                    </div>
                    <div className="employee-meta">
                      <div className="employee-name">{u.name || `User #${u.id}`}</div>
                      <div className="employee-subtitle">Permission access control</div>
                    </div>
                  </div>
                  <div className="employee-actions">
                    <div className="employee-role">{u.role || "Employee"}</div>
                    <button
                      type="button"
                      className={`permission-btn${openPanel?.userId === u.id && openPanel?.type === "permissions" ? " is-active" : ""}`}
                      onClick={() => openPermission(u, "permissions")}
                    >
                      {openPanel?.userId === u.id && openPanel?.type === "permissions" ? (
                        <>
                          <ChevronUp size={16} strokeWidth={2} />
                          <span>Hide Permissions</span>
                        </>
                      ) : (
                        <>
                          <ChevronDown size={16} strokeWidth={2} />
                          <span>View Permissions</span>
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className={`permission-btn app-permission-btn${openPanel?.userId === u.id && openPanel?.type === "app_permissions" ? " is-active" : ""}`}
                      onClick={() => openPermission(u, "app_permissions")}
                    >
                      {openPanel?.userId === u.id && openPanel?.type === "app_permissions" ? (
                        <>
                          <ChevronUp size={16} strokeWidth={2} />
                          <span>Hide App Permissions</span>
                        </>
                      ) : (
                        <>
                          <ChevronDown size={16} strokeWidth={2} />
                          <span>App Permissions</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {openPanel?.userId === u.id && (
                  <div className="permission-panel">
                    <div className="permission-panel-head">
                      <h3 className="permission-panel-title">
                        {openPanel.type === "app_permissions" ? "Mobile App Permissions" : "Web/Admin Permissions"}
                      </h3>
                      <span className="permission-panel-badge">
                        {openPanel.type === "app_permissions" ? "APK Access" : "Panel Access"}
                      </span>
                    </div>
                    <div className="permission-grid">
                      {permissionPagesToRender.map((page) => (
                        <label key={page.id} className="permission-checkbox">
                          <input
                            type="checkbox"
                            checked={permissions.includes(page.id)}
                            onChange={() => togglePermission(page.id)}
                          />
                          <span className="checkbox-label">{openPanel.type === "app_permissions" ? page.label : getPermissionLabel(page.id)}</span>
                        </label>
                      ))}
                    </div>
                    <div className="permission-actions">
                      <button type="button" className="btn btn-save-permissions" onClick={savePermissions}>
                        <Save size={16} strokeWidth={2} />
                        <span>{openPanel.type === "app_permissions" ? "Save App Permissions" : "Save Permissions"}</span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={() => {
                          setOpenPanel(null);
                          setSelectedUser(null);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="empty-state">
              <p>No employees found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
